/* eslint-disable react/prop-types */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import saveAs from 'file-saver';

import { Plugin, Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core';
import {
  EditingState,
  PagingState,
  IntegratedPaging,
  SortingState,
  IntegratedSorting,
  SearchState,
  IntegratedFiltering,
  SelectionState,
  IntegratedSelection
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';

import {
  Grid,
  Table,
  TableHeaderRow,
  TableEditColumn,
  PagingPanel,
  TableColumnResizing,
  DragDropProvider,
  TableColumnReordering,
  SearchPanel,
  Toolbar,
  TableSelection,
  ColumnChooser,
  TableColumnVisibility,
  ExportPanel,
  TableInlineCellEditing
} from '@devexpress/dx-react-grid-material-ui';

import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import ClassDialogContent from './ClassDialogContent';

import HeaderFilterCell, {
  getRowCellValue,
  HeaderFilterContext
} from '../../CustomerNew/table/customTemplate/HeaderFilterCell';
import { useLocalStorage } from 'react-use';
import KebabCommandCell from '../../Vendor/table/customTemplate/KebabCommandCell';
import CustomToolbarRoot from '../../UserNew/table/customTemplate/CustomToolbarRoot';
import classService from '../../../services/ClassService';
import { toast } from 'react-hot-toast';
import ResponsiveTableContainer, { StickyTableHead } from '../../common/ResponsiveTableContainer';

/* eslint-disable no-shadow */

const Popup = ({ row, onChange, onApplyChanges, onCancelChanges, open }) => {
  return (
    <Dialog open={open} onClose={onCancelChanges} aria-labelledby="form-dialog-title" fullWidth maxWidth="md">
      <ClassDialogContent
        row={row}
        onCancelChanges={onCancelChanges}
        onChange={onChange}
        onApplyChanges={onApplyChanges}
      />
    </Dialog>
  );
};

// eslint-disable-next-line react/display-name
const PopupEditing = React.memo(({ popupComponent: Popup }) => (
  <Plugin>
    <Template name="popupEditing">
      <TemplateConnector>
        {(
          { rows, getRowId, addedRows, editingRowIds, createRowChange, rowChanges },
          {
            changeRow,
            changeAddedRow,
            commitChangedRows,
            commitAddedRows,
            stopEditRows,
            cancelAddedRows,
            cancelChangedRows
          }
        ) => {
          const isNew = addedRows.length > 0;
          let editedRow;
          let rowId;
          if (isNew) {
            rowId = 0;
            editedRow = addedRows[rowId];
          } else {
            [rowId] = editingRowIds;
            const targetRow = rows.filter((row) => getRowId(row) === rowId)[0];
            editedRow = { ...targetRow, ...rowChanges[rowId] };
          }

          const processValueChange = ({ target: { name, value } }) => {
            const changeArgs = {
              rowId,
              change: createRowChange(editedRow, value, name)
            };
            if (isNew) {
              changeAddedRow(changeArgs);
            } else {
              changeRow(changeArgs);
            }
          };
          const rowIds = isNew ? [0] : editingRowIds;
          const applyChanges = () => {
            if (isNew) {
              commitAddedRows({ rowIds });
            } else {
              stopEditRows({ rowIds });
              commitChangedRows({ rowIds });

              //   TODO : change the logic to update

              //   //Important : Always include sparse true, Id  , SyncToken, FullyQualifiedName, DisplayName,
              //   let customerPayload = {
              //     Id: editedRow.id,
              //     sparse: true,
              //     SyncToken: editedRow.SyncToken
              //   };

              //   //update customerpayload according to quickbook
              //   let updateCustomerPayload;
              //   if (rowChanges[rowId]) {
              //     updateCustomerPayload = getFormattedCustomerPayload(customerPayload, rowChanges[rowId]);
              //   }

              //   clientService
              //     .updateQuickBookCustomer(updateCustomerPayload)
              //     .then(() => {
              //       toast.success('Successfully updated customer in quickbook');
              //     })
              //     .catch((error) => {
              //       toast.error('Could not update customer in quickbook' || error);
              //     });
            }
          };
          const cancelChanges = () => {
            if (isNew) {
              cancelAddedRows({ rowIds });
            } else {
              stopEditRows({ rowIds });
              cancelChangedRows({ rowIds });
            }
          };

          const open = editingRowIds.length > 0 || isNew;
          return (
            <Popup
              open={open}
              row={editedRow}
              onChange={processValueChange}
              onApplyChanges={applyChanges}
              onCancelChanges={cancelChanges}
            />
          );
        }}
      </TemplateConnector>
    </Template>
    <Template name="root">
      <TemplatePlaceholder />
      <TemplatePlaceholder name="popupEditing" />
    </Template>
  </Plugin>
));

const getRowId = (row) => row.id;

const CLASS_INLINE_COLUMNS = ['Name'];

const ClassFocusableCell = ({ onClick, ...restProps }) => {
  const handleFocus = (event) => {
    if (event.currentTarget !== event.target) return;
    onClick();
  };

  return <Table.Cell {...restProps} tabIndex={0} onFocus={handleFocus} />;
};

// Single-clicking a supported cell starts inline editing. Double-clicking a
// data row clears the cell editor and opens the complete class dialog.
const ClickableClassRow = ({ row, onOpen, ...restProps }) => {
  const handleDoubleClick = (event) => {
    if (
      !row ||
      event.target.closest('button, a, input[type="checkbox"], [role="checkbox"], [role="button"], [role="menuitem"]')
    )
      return;
    onOpen(getRowId(row));
  };

  const handleKeyDown = (event) => {
    if (event.currentTarget !== event.target) return;
    if (!row || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onOpen(getRowId(row));
  };

  return (
    <Table.Row
      {...restProps}
      row={row}
      tabIndex={row ? 0 : undefined}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      aria-label={row ? `Open ${row.Name || 'class'} details` : undefined}
      style={{
        ...restProps.style,
        cursor: row ? 'pointer' : restProps.style?.cursor
      }}
    />
  );
};

const onSave = (workbook) => {
  workbook.xlsx.writeBuffer().then((buffer) => {
    // eslint-disable-next-line no-undef
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
  });
};

export default function ClassTable({ rows, setRows, selection, setSelection }) {
  const [columns] = useState([
    { name: 'FullyQualifiedName', title: 'FullyQualifiedName' },
    { name: 'Name', title: 'Name' },
    { name: 'id', title: 'ClassId' },
    { name: 'classStatus', title: 'Action' },
    { name: 'SyncToken', title: 'SyncToken' },
    { name: 'SubClass', title: 'SubClass' },
    { name: 'sparse', title: 'sparse' }
  ]);

  //it was using useLocalStorage classColumnWidths, update it once customer columns are done
  const [classColumnWidths, setClassColumnWidths] = useState([
    { columnName: 'FullyQualifiedName', width: 180 },
    { columnName: 'Name', width: 180 },
    { columnName: 'id', width: 180 },
    { columnName: 'classStatus', width: 180 },
    { columnName: 'SyncToken', width: 180 },
    { columnName: 'SubClass', width: 180 },
    { columnName: 'sparse', width: 180 }
  ]);
  const [classColumnOrder, setClassColumnOrder] = useState([
    'FullyQualifiedName',
    'Name',
    'id',
    'classStatus',
    'SyncToken',
    'SubClass',
    'sparse'
  ]);

  const [sorting, setSorting] = useState([{ columnName: 'FullyQualifiedName', direction: 'asc' }]);
  const [hiddenColumnNames, setHiddenColumnNames] = useState(['']);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [pageSizes] = useState([100, 200, 300]);
  const [searchValue, setSearchState] = useState('');

  const [columnFilters, setColumnFilters] = useLocalStorage('classColumnFilters', {});

  const editingModeRef = useRef(null);
  const [editingCellIds, setEditingCellIds] = useState([]);
  const [editingRowIds, setEditingRowIds] = useState([]);

  const changeEditingMode = useCallback((mode) => {
    editingModeRef.current = mode;
  }, []);

  const startPopupEditing = useCallback(() => {
    setEditingCellIds([]);
    changeEditingMode('popup');
  }, [changeEditingMode]);

  const openClassDetails = useCallback(
    (rowId) => {
      setEditingCellIds([]);
      changeEditingMode('popup');
      setEditingRowIds([rowId]);
    },
    [changeEditingMode]
  );

  const editingStateColumnExtensions = useMemo(
    () => columns.map(({ name }) => ({ columnName: name, editingEnabled: CLASS_INLINE_COLUMNS.includes(name) })),
    [columns]
  );

  const commitChanges = ({ changed }) => {
    if (!changed || Object.keys(changed).length === 0) return;

    const changedRows = rows.map((row) => (changed[row.id] ? { ...row, ...changed[row.id] } : row));
    const updates = Object.keys(changed).map((rowId) => {
      const updatedRow = changedRows.find((row) => String(row.id) === String(rowId));
      if (!updatedRow) throw new Error(`Class row ${rowId} was not found`);

      return classService.updateClassInQbo({
        Id: updatedRow.Id || updatedRow.id,
        SyncToken: updatedRow.SyncToken,
        Name: updatedRow.Name,
        sparse: true
      });
    });

    setRows(changedRows);

    // eslint-disable-next-line no-undef
    Promise.all(updates)
      .then((responses) => {
        const qboClasses = responses.map((response) => response?.data?.data).filter(Boolean);
        setRows((currentRows) =>
          currentRows.map((row) => {
            const qboClass = qboClasses.find((item) => String(item.Id) === String(row.id));
            return qboClass
              ? {
                  ...row,
                  ...qboClass,
                  id: qboClass.Id,
                  classStatus: row.classStatus
                }
              : row;
          })
        );
        toast.success('Successfully updated class in quickbook');
      })
      .catch((error) => {
        setRows(rows);
        toast.error(error?.response?.data?.message || error?.message || 'Could not update class in quickbook');
      });
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      // Column header filters
      for (const columnName of Object.keys(columnFilters)) {
        const activeVals = columnFilters[columnName];
        if (activeVals && activeVals.length > 0) {
          const rowVal = getRowCellValue(row, columnName);
          if (!activeVals.includes(rowVal)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [rows, columnFilters]);

  const exporterRef = useRef(null);

  const startExport = useCallback(
    (options) => {
      exporterRef.current.exportGrid(options);
    },
    [exporterRef]
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>Total rows selected: {selection.length}</div>
      </div>

      <HeaderFilterContext.Provider value={{ rows, columnFilters, onFilterChange: setColumnFilters }}>
        <Paper>
          <Grid rows={filteredRows} columns={columns} getRowId={getRowId}>
            <EditingState
              editingCellIds={editingCellIds}
              onEditingCellIdsChange={(cellIds) => {
                if (editingModeRef.current !== 'popup' || editingModeRef.current === null) {
                  const editableCellIds = cellIds.filter(({ columnName }) => CLASS_INLINE_COLUMNS.includes(columnName));
                  changeEditingMode(editableCellIds.length ? 'inline' : null);
                  setEditingCellIds(editableCellIds);
                }
              }}
              editingRowIds={editingRowIds}
              onEditingRowIdsChange={(rowIds) => {
                setEditingRowIds(rowIds);
                if (rowIds.length) {
                  setEditingCellIds([]);
                  changeEditingMode('popup');
                } else if (editingModeRef.current === 'popup') {
                  changeEditingMode(null);
                }
              }}
              onCommitChanges={commitChanges}
              columnExtensions={editingStateColumnExtensions}
            />
            <PagingState
              currentPage={currentPage}
              onCurrentPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
            <SelectionState selection={selection} onSelectionChange={setSelection} />
            <IntegratedSelection />

            <DragDropProvider />

            <SortingState sorting={sorting} onSortingChange={setSorting} />
            <IntegratedSorting />

            <SearchState value={searchValue} onValueChange={setSearchState} />
            <IntegratedFiltering />
            <IntegratedPaging />

            <Table
              rowComponent={(props) => <ClickableClassRow {...props} onOpen={openClassDetails} />}
              cellComponent={ClassFocusableCell}
              containerComponent={ResponsiveTableContainer}
              headComponent={StickyTableHead}
            />
            <TableColumnResizing columnWidths={classColumnWidths} onColumnWidthsChange={setClassColumnWidths} />
            <TableColumnReordering order={classColumnOrder} onOrderChange={setClassColumnOrder} />

            <TableHeaderRow showSortingControls cellComponent={HeaderFilterCell} />

            <TableSelection showSelectAll />
            <PagingPanel pageSizes={pageSizes} />

            {/* Native grid edit commands, displayed through the shared KebabCommandCell. */}
            <TableEditColumn
              width={90}
              showEditCommand
              cellComponent={(props) => <KebabCommandCell {...props} onPopupEditStart={startPopupEditing} />}
            />

            <TableInlineCellEditing startEditAction="click" selectTextOnEditStart={true} />

            <PopupEditing popupComponent={Popup} />
            <TableColumnVisibility
              hiddenColumnNames={hiddenColumnNames}
              onHiddenColumnNamesChange={setHiddenColumnNames}
            />

            {/* Pass the custom root to the Toolbar */}
            <Toolbar rootComponent={CustomToolbarRoot} />

            <SearchPanel />
            <ColumnChooser />
            <ExportPanel startExport={startExport} />
          </Grid>
          <GridExporter
            ref={exporterRef}
            columns={columns}
            rows={filteredRows}
            selection={selection}
            onSave={onSave}
            getRowId={getRowId}
            hiddenColumnNames={hiddenColumnNames}
          />
        </Paper>
      </HeaderFilterContext.Provider>
    </div>
  );
}
